import { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCircleNotch,
  faCheck,
  faLink,
  faVideo,
  faExternalLinkAlt,
  faExclamationCircle,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { API_NEW_URL } from "../../constants/constants";
import AuthContext from "../../store/auth-context";
import classes from "./OAuth.module.css";

/**
 * OAuthAuthorize Component
 *
 * Shows different UI based on user role:
 * - Admin: "Authorize Blitzz" button that starts OAuth flow
 * - Non-Admin: Message asking them to contact admin
 */
const OAuthAuthorize = ({ isAdmin, subdomain, token, onAuthComplete }) => {
  const authCtx = useContext(AuthContext);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Use token prop if provided, otherwise fall back to authCtx.token
  const authToken = token || authCtx.token;

  /**
   * Start the OAuth authorization flow
   * Opens Zendesk OAuth page in a popup or redirects
   */
  const handleAuthorize = async () => {
    setIsAuthorizing(true);
    setAuthError(null);

    try {
      // Build the authorization URL (using new API structure)
      const authorizeUrl =
        `${API_NEW_URL}zendesk/oauth/authorize` +
        `?subdomain=${encodeURIComponent(subdomain)}`;
      console.log("authorizeUrl",authorizeUrl);
      // Option 1: Open in popup (recommended for better UX)
      const popup = window.open(
        authorizeUrl,
        "ZendeskOAuth",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );

      // Listen for popup close or message from callback
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          // Check if authorization was successful
          checkAuthorizationComplete();
        }
      }, 3000);

    } catch (error) {
      console.error("Error starting OAuth flow:", error);
      setAuthError("Failed to start authorization. Please try again.");
      setIsAuthorizing(false);
    }
  };

  /**
   * Check if authorization completed successfully
   * Called after popup closes
   */
  const checkAuthorizationComplete = async () => {
    try {
      const response = await fetch(
        `${API_NEW_URL}zendesk/oauth/status?subdomain=${subdomain}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`,
          },
        }
      );

      const data = await response.json();

      if (data?.hasTokens) {
        // Success! OAuth completed
        onAuthComplete();
      } else {
        console.error("Error checking zendesk status inside checkAuthorizationComplete:", data);
        // Authorization was cancelled or failed
        setAuthError(data?.error?.message || "Authorization was not completed. Please try again.");
        setIsAuthorizing(false);
      }
    } catch (error) {
      console.error("Error checking authorization:", error);
      setAuthError("Failed to verify authorization. Please refresh the page.");
      setIsAuthorizing(false);
    }
  };

  // Render for Admin users - Improved Design
  if (isAdmin) {
    return (
      <div className={classes.authContainer}>
        {/* Value Proposition */}
        <div className={classes.valueProp}>
          <div className={classes.valueIcon}>
            <FontAwesomeIcon icon={faVideo} className={classes.videoIcon} />
          </div>
          <h3 className={classes.valueTitle}>Set Up Blitzz Video Support</h3>
          <p className={classes.valueDesc}>
            Authorize Blitzz to enable live video support sessions for your team directly from Zendesk tickets.
          </p>
        </div>

        {/* Permissions Box */}
        <div className={classes.permissionsBox}>
          <p className={classes.permissionsTitle}>Your team will be able to:</p>
          <ul className={classes.permissionsList}>
            <li>
              <FontAwesomeIcon icon={faCheck} className={classes.checkIcon} />
              Send video session invites to customers
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} className={classes.checkIcon} />
              Add session images and notes to tickets
            </li>
          </ul>
        </div>

        {/* Error Message */}
        {authError && (
          <div className={classes.errorMessage}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span style={{ marginLeft: "8px" }}>{authError}</span>
          </div>
        )}

        {/* Authorize Button */}
        <button
          className={classes.authorizeBtn}
          onClick={handleAuthorize}
          disabled={isAuthorizing}
        >
          {isAuthorizing ? (
            <>
              <FontAwesomeIcon icon={faCircleNotch} spin />
              Authorizing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faLink} />
              Authorize Blitzz
            </>
          )}
        </button>

        {/* Footer Note */}
        <p className={classes.footerNote}>This is a one-time authorization.</p>

        {/* Learn More Link */}
        <div className={classes.learnMore}>
          <a href="https://blitzz.co" target="_blank" rel="noopener noreferrer">
            Learn more about Blitzz <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "10px" }} />
          </a>
        </div>
      </div>
    );
  }

  // Render for Non-Admin users - Improved Design
  return (
    <div className={classes.authContainer}>
      {/* Status Icon */}
      <div className={classes.statusIcon}>
        <FontAwesomeIcon icon={faExclamationCircle} className={classes.statusIconInner} />
      </div>

      {/* Title and Description */}
      <h3 className={classes.statusTitle}>Authorization Required</h3>
      <p className={classes.statusDesc}>
        Blitzz lets you start live video support sessions with customers directly from Zendesk.
      </p>

      {/* Info Box */}
      <div className={classes.infoBoxNew}>
        <p className={classes.infoTitle}>What you'll be able to do</p>
        <p className={classes.infoText}>
          Launch video calls, share screens, and automatically save session images and notes to tickets.
        </p>
      </div>

      {/* Admin Action Box */}
      <div className={classes.adminAction}>
        <p className={classes.adminActionTitle}>
          <FontAwesomeIcon icon={faUserShield} className={classes.adminActionIcon} />
          Admin action needed
        </p>
        <p className={classes.adminActionText}>
          A Zendesk admin needs to authorize Blitzz before you can use it.
        </p>
      </div>

      {/* Learn More Link */}
      <div className={classes.learnMore}>
        <a href="https://blitzz.co" target="_blank" rel="noopener noreferrer">
          Learn more about Blitzz <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "10px" }} />
        </a>
      </div>
    </div>
  );
};

export default OAuthAuthorize;
